import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Activity, Building2, CreditCard, Landmark, Loader2, Pencil, Plus,
  RefreshCw, ShieldCheck, Trash2, Wallet, KeyRound,
} from "lucide-react";

const STATUS_BADGE = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  error: "bg-red-50 text-red-700 border-red-200",
  unchecked: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

const errMsg = (e, fallback) => e.response?.data?.detail || fallback;

/* ══ Stripe ═════════════════════════════════════════════════════ */
const EMPTY_STRIPE = {
  name: "", xero_tenant_id: "", xero_tenant_name: "",
  mode: "test", publishable_key: "", secret_key: "", webhook_secret: "",
};

function StripeConfigDialog({ dialog, setDialog, reload, tenants }) {
  const [saving, setSaving] = useState(false);
  if (!dialog) return null;
  const { mode, form } = dialog;
  const set = (k, v) => setDialog({ ...dialog, form: { ...form, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // If picking from the Xero tenant list, also stash the name for display
      if (form.xero_tenant_id) {
        const t = tenants.find((x) => x.tenant_id === form.xero_tenant_id);
        payload.xero_tenant_name = t?.tenant_name || form.xero_tenant_name || "";
      }
      if (mode === "add") await api.post("/merchant-settings/stripe", payload);
      else await api.put(`/merchant-settings/stripe/${dialog.config_id}`, payload);
      toast.success(mode === "add" ? "Stripe config saved" : "Stripe config updated");
      setDialog(null); reload();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={() => setDialog(null)}>
      <DialogContent className="max-w-lg" data-testid="merchant-stripe-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "add" ? "Add Stripe configuration" : "Edit Stripe configuration"}</DialogTitle>
          <DialogDescription>
            Each Xero company has its own Stripe account. Keys are encrypted at rest and never shown again.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <Input placeholder="Config name (e.g. Iceberg Digital · Live)" value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="merchant-stripe-name-input" />
          <div>
            <div className="text-xs font-semibold text-zinc-600 mb-1">Xero tenant (company)</div>
            <Select value={form.xero_tenant_id || "none"}
              onValueChange={(v) => set("xero_tenant_id", v === "none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="merchant-stripe-tenant-select">
                <SelectValue placeholder="— not linked —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— not linked —</SelectItem>
                {[...tenants]
                  .sort((a, b) => (a.tenant_name || "").localeCompare(b.tenant_name || ""))
                  .map((t) => (
                    <SelectItem key={t.tenant_id} value={t.tenant_id}>
                      {t.tenant_name || t.tenant_id}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {!tenants.length && (
              <div className="text-[11px] text-amber-700 mt-1">
                Connect Xero first (Finance → Xero master) to see the list of tenants here.
              </div>
            )}
          </div>
          <div>
            <div className="text-xs font-semibold text-zinc-600 mb-1">Mode</div>
            <Select value={form.mode || "test"} onValueChange={(v) => set("mode", v)}>
              <SelectTrigger className="h-9" data-testid="merchant-stripe-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="test">Test</SelectItem>
                <SelectItem value="live">Live</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input placeholder="Publishable key (pk_test_… or pk_live_…)"
            value={form.publishable_key || ""}
            onChange={(e) => set("publishable_key", e.target.value)}
            data-testid="merchant-stripe-pk-input" />
          <Input type="password"
            placeholder={mode === "edit" ? "New secret key (leave blank to keep current)" : "Secret key (sk_test_… or sk_live_…)"}
            value={form.secret_key || ""}
            onChange={(e) => set("secret_key", e.target.value)}
            data-testid="merchant-stripe-sk-input" />
          <Input type="password"
            placeholder={mode === "edit" ? "New webhook secret (leave blank to keep current)" : "Webhook signing secret (whsec_…)"}
            value={form.webhook_secret || ""}
            onChange={(e) => set("webhook_secret", e.target.value)}
            data-testid="merchant-stripe-whsec-input" />
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving || !form.name}
            data-testid="merchant-stripe-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StripeTab({ tenants }) {
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState("");

  const reload = useCallback(async () => {
    try { const { data } = await api.get("/merchant-settings/stripe"); setRows(data); }
    catch { /* toast handled globally */ }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const test = async (r) => {
    setBusy(r.config_id);
    try {
      const { data } = await api.post(`/merchant-settings/stripe/${r.config_id}/test`);
      if (data.status === "ok") toast.success(data.detail || "Connected");
      else toast.error(data.detail || "Connection failed");
      reload();
    } catch (e) { toast.error(errMsg(e, "Test failed")); }
    finally { setBusy(""); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete Stripe config "${r.name}"?`)) return;
    try {
      await api.delete(`/merchant-settings/stripe/${r.config_id}`);
      toast.success("Removed"); reload();
    } catch (e) { toast.error(errMsg(e, "Delete failed")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-500">
          {rows.length} Stripe configuration{rows.length === 1 ? "" : "s"} — one per Xero tenant is recommended.
        </div>
        <Button size="sm"
          onClick={() => setDialog({ mode: "add", form: { ...EMPTY_STRIPE } })}
          data-testid="merchant-stripe-add-btn">
          <Plus className="h-4 w-4 mr-1" />Add Stripe config
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {rows.map((r) => (
          <Card key={r.config_id} data-testid={`merchant-stripe-card-${r.config_id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <CreditCard className="h-4 w-4 text-zinc-400 shrink-0" />
                  <div className="font-semibold truncate">{r.name}</div>
                </div>
                <Badge variant="outline" className={STATUS_BADGE[r.status] || STATUS_BADGE.unchecked}>
                  {r.status}
                </Badge>
              </div>
              <div className="text-xs text-zinc-500 flex flex-wrap gap-2">
                {r.xero_tenant_name ? (
                  <span className="inline-flex items-center gap-1">
                    <Building2 className="h-3 w-3" />{r.xero_tenant_name}
                  </span>
                ) : (
                  <span className="text-amber-700">not linked to a Xero tenant</span>
                )}
                <Badge variant="outline"
                  className={r.mode === "live"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]"
                    : "bg-amber-50 text-amber-700 border-amber-200 text-[10px]"}>
                  {r.mode === "live" ? "LIVE" : "TEST"}
                </Badge>
              </div>
              <div className="text-[11px] text-zinc-500 font-mono">
                pk …{r.publishable_key_last4 || "—"} · sk …{r.secret_key_last4 || "—"} · whsec {r.webhook_secret_set ? "set" : "not set"}
              </div>
              {r.status_detail && (
                <div className={`text-xs line-clamp-2 ${r.status === "ok" ? "text-emerald-700" : "text-red-600"}`}>
                  {r.status_detail}
                </div>
              )}
              <div className="flex items-center gap-1.5 pt-1">
                <Button variant="outline" size="sm" disabled={busy === r.config_id}
                  onClick={() => test(r)}
                  data-testid={`merchant-stripe-test-${r.config_id}`}>
                  {busy === r.config_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />}
                  <span className="ml-1">Test</span>
                </Button>
                <Button variant="ghost" size="sm"
                  onClick={() => setDialog({ mode: "edit", config_id: r.config_id, form: { ...EMPTY_STRIPE, ...r, publishable_key: "", secret_key: "", webhook_secret: "" } })}
                  data-testid={`merchant-stripe-edit-${r.config_id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600"
                  onClick={() => remove(r)}
                  data-testid={`merchant-stripe-delete-${r.config_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {!rows.length && (
          <Card className="md:col-span-2 border-dashed">
            <CardContent className="p-10 text-center text-zinc-400 text-sm">
              <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No Stripe configurations yet. Add one per Xero tenant so invoices are paid into the right merchant account.
            </CardContent>
          </Card>
        )}
      </div>
      <StripeConfigDialog dialog={dialog} setDialog={setDialog} reload={reload} tenants={tenants} />
    </div>
  );
}

/* ══ Simplify (Commbank) ═══════════════════════════════════════ */
const EMPTY_SIMPLIFY = { name: "", mode: "sandbox", public_key: "", private_key: "" };

function SimplifyDialog({ dialog, setDialog, reload }) {
  const [saving, setSaving] = useState(false);
  if (!dialog) return null;
  const { mode, form } = dialog;
  const set = (k, v) => setDialog({ ...dialog, form: { ...form, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      if (mode === "add") await api.post("/merchant-settings/simplify", form);
      else await api.put(`/merchant-settings/simplify/${dialog.config_id}`, form);
      toast.success(mode === "add" ? "Simplify config saved" : "Simplify config updated");
      setDialog(null); reload();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={() => setDialog(null)}>
      <DialogContent className="max-w-lg" data-testid="merchant-simplify-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "add" ? "Add Commbank Simplify" : "Edit Commbank Simplify"}</DialogTitle>
          <DialogDescription>
            Commbank Simplify Commerce (Mastercard). Keys are visible in the Simplify dashboard under Account → API Keys.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Config name" value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="merchant-simplify-name-input" />
          <div>
            <div className="text-xs font-semibold text-zinc-600 mb-1">Mode</div>
            <Select value={form.mode || "sandbox"} onValueChange={(v) => set("mode", v)}>
              <SelectTrigger className="h-9" data-testid="merchant-simplify-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sandbox">Sandbox</SelectItem>
                <SelectItem value="live">Live</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input placeholder="Public key (sbpb_… or lvpb_…)"
            value={form.public_key || ""}
            onChange={(e) => set("public_key", e.target.value)}
            data-testid="merchant-simplify-pub-input" />
          <Input type="password"
            placeholder={mode === "edit" ? "New private key (leave blank to keep current)" : "Private key"}
            value={form.private_key || ""}
            onChange={(e) => set("private_key", e.target.value)}
            data-testid="merchant-simplify-priv-input" />
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving || !form.name}
            data-testid="merchant-simplify-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SimplifyTab() {
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState("");

  const reload = useCallback(async () => {
    try { const { data } = await api.get("/merchant-settings/simplify"); setRows(data); }
    catch { /* noop */ }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const test = async (r) => {
    setBusy(r.config_id);
    try {
      const { data } = await api.post(`/merchant-settings/simplify/${r.config_id}/test`);
      if (data.status === "ok") toast.success("Connected");
      else toast.error(data.detail || "Connection failed");
      reload();
    } catch (e) { toast.error(errMsg(e, "Test failed")); }
    finally { setBusy(""); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete Simplify config "${r.name}"?`)) return;
    try {
      await api.delete(`/merchant-settings/simplify/${r.config_id}`);
      toast.success("Removed"); reload();
    } catch (e) { toast.error(errMsg(e, "Delete failed")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-500">
          {rows.length} Commbank Simplify configuration{rows.length === 1 ? "" : "s"}.
        </div>
        <Button size="sm"
          onClick={() => setDialog({ mode: "add", form: { ...EMPTY_SIMPLIFY } })}
          data-testid="merchant-simplify-add-btn">
          <Plus className="h-4 w-4 mr-1" />Add Simplify config
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {rows.map((r) => (
          <Card key={r.config_id} data-testid={`merchant-simplify-card-${r.config_id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Landmark className="h-4 w-4 text-zinc-400 shrink-0" />
                  <div className="font-semibold truncate">{r.name}</div>
                </div>
                <Badge variant="outline" className={STATUS_BADGE[r.status] || STATUS_BADGE.unchecked}>
                  {r.status}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <Badge variant="outline"
                  className={r.mode === "live"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]"
                    : "bg-amber-50 text-amber-700 border-amber-200 text-[10px]"}>
                  {r.mode === "live" ? "LIVE" : "SANDBOX"}
                </Badge>
                <span className="font-mono">
                  pub …{r.public_key_last4 || "—"} · priv {r.private_key_set ? "set" : "not set"}
                </span>
              </div>
              {r.status_detail && (
                <div className={`text-xs line-clamp-2 ${r.status === "ok" ? "text-emerald-700" : "text-red-600"}`}>
                  {r.status_detail}
                </div>
              )}
              <div className="flex items-center gap-1.5 pt-1">
                <Button variant="outline" size="sm" disabled={busy === r.config_id}
                  onClick={() => test(r)}
                  data-testid={`merchant-simplify-test-${r.config_id}`}>
                  {busy === r.config_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />}
                  <span className="ml-1">Test</span>
                </Button>
                <Button variant="ghost" size="sm"
                  onClick={() => setDialog({ mode: "edit", config_id: r.config_id, form: { ...EMPTY_SIMPLIFY, ...r, public_key: "", private_key: "" } })}
                  data-testid={`merchant-simplify-edit-${r.config_id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600"
                  onClick={() => remove(r)}
                  data-testid={`merchant-simplify-delete-${r.config_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {!rows.length && (
          <Card className="md:col-span-2 border-dashed">
            <CardContent className="p-10 text-center text-zinc-400 text-sm">
              <Landmark className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No Commbank Simplify configurations yet.
            </CardContent>
          </Card>
        )}
      </div>
      <SimplifyDialog dialog={dialog} setDialog={setDialog} reload={reload} />
    </div>
  );
}

/* ══ Westpac PayWay ════════════════════════════════════════════ */
const EMPTY_PAYWAY = {
  name: "", mode: "test", merchant_id: "", bank_account_id: "",
  publishable_key: "", secret_key: "",
};

function PayWayDialog({ dialog, setDialog, reload }) {
  const [saving, setSaving] = useState(false);
  if (!dialog) return null;
  const { mode, form } = dialog;
  const set = (k, v) => setDialog({ ...dialog, form: { ...form, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      if (mode === "add") await api.post("/merchant-settings/payway", form);
      else await api.put(`/merchant-settings/payway/${dialog.config_id}`, form);
      toast.success(mode === "add" ? "PayWay config saved" : "PayWay config updated");
      setDialog(null); reload();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={() => setDialog(null)}>
      <DialogContent className="max-w-lg" data-testid="merchant-payway-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "add" ? "Add Westpac PayWay" : "Edit Westpac PayWay"}</DialogTitle>
          <DialogDescription>
            REST API keys — inside PayWay go to Settings → REST API Keys. Base URL: api.payway.com.au.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Config name" value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="merchant-payway-name-input" />
          <div>
            <div className="text-xs font-semibold text-zinc-600 mb-1">Mode</div>
            <Select value={form.mode || "test"} onValueChange={(v) => set("mode", v)}>
              <SelectTrigger className="h-9" data-testid="merchant-payway-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="test">Test</SelectItem>
                <SelectItem value="live">Live</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Merchant ID (e.g. TEST)" value={form.merchant_id || ""}
              onChange={(e) => set("merchant_id", e.target.value)}
              data-testid="merchant-payway-mid-input" />
            <Input placeholder="Bank account ID" value={form.bank_account_id || ""}
              onChange={(e) => set("bank_account_id", e.target.value)}
              data-testid="merchant-payway-bank-input" />
          </div>
          <Input placeholder="Publishable API key" value={form.publishable_key || ""}
            onChange={(e) => set("publishable_key", e.target.value)}
            data-testid="merchant-payway-pub-input" />
          <Input type="password"
            placeholder={mode === "edit" ? "New secret API key (leave blank to keep current)" : "Secret API key"}
            value={form.secret_key || ""}
            onChange={(e) => set("secret_key", e.target.value)}
            data-testid="merchant-payway-sec-input" />
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving || !form.name}
            data-testid="merchant-payway-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayWayTab() {
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState("");

  const reload = useCallback(async () => {
    try { const { data } = await api.get("/merchant-settings/payway"); setRows(data); }
    catch { /* noop */ }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const test = async (r) => {
    setBusy(r.config_id);
    try {
      const { data } = await api.post(`/merchant-settings/payway/${r.config_id}/test`);
      if (data.status === "ok") toast.success("Connected");
      else toast.error(data.detail || "Connection failed");
      reload();
    } catch (e) { toast.error(errMsg(e, "Test failed")); }
    finally { setBusy(""); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete PayWay config "${r.name}"?`)) return;
    try {
      await api.delete(`/merchant-settings/payway/${r.config_id}`);
      toast.success("Removed"); reload();
    } catch (e) { toast.error(errMsg(e, "Delete failed")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-500">
          {rows.length} Westpac PayWay configuration{rows.length === 1 ? "" : "s"}.
        </div>
        <Button size="sm"
          onClick={() => setDialog({ mode: "add", form: { ...EMPTY_PAYWAY } })}
          data-testid="merchant-payway-add-btn">
          <Plus className="h-4 w-4 mr-1" />Add PayWay config
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {rows.map((r) => (
          <Card key={r.config_id} data-testid={`merchant-payway-card-${r.config_id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Wallet className="h-4 w-4 text-zinc-400 shrink-0" />
                  <div className="font-semibold truncate">{r.name}</div>
                </div>
                <Badge variant="outline" className={STATUS_BADGE[r.status] || STATUS_BADGE.unchecked}>
                  {r.status}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-xs text-zinc-500 flex-wrap">
                <Badge variant="outline"
                  className={r.mode === "live"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]"
                    : "bg-amber-50 text-amber-700 border-amber-200 text-[10px]"}>
                  {r.mode === "live" ? "LIVE" : "TEST"}
                </Badge>
                {r.merchant_id && (
                  <span className="font-mono">merchant {r.merchant_id}</span>
                )}
                {r.bank_account_id && (
                  <span className="font-mono">bank {r.bank_account_id}</span>
                )}
              </div>
              <div className="text-[11px] text-zinc-500 font-mono">
                pub …{r.publishable_key_last4 || "—"} · secret {r.secret_key_set ? "set" : "not set"}
              </div>
              {r.status_detail && (
                <div className={`text-xs line-clamp-2 ${r.status === "ok" ? "text-emerald-700" : "text-red-600"}`}>
                  {r.status_detail}
                </div>
              )}
              <div className="flex items-center gap-1.5 pt-1">
                <Button variant="outline" size="sm" disabled={busy === r.config_id}
                  onClick={() => test(r)}
                  data-testid={`merchant-payway-test-${r.config_id}`}>
                  {busy === r.config_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />}
                  <span className="ml-1">Test</span>
                </Button>
                <Button variant="ghost" size="sm"
                  onClick={() => setDialog({ mode: "edit", config_id: r.config_id, form: { ...EMPTY_PAYWAY, ...r, publishable_key: "", secret_key: "" } })}
                  data-testid={`merchant-payway-edit-${r.config_id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600"
                  onClick={() => remove(r)}
                  data-testid={`merchant-payway-delete-${r.config_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {!rows.length && (
          <Card className="md:col-span-2 border-dashed">
            <CardContent className="p-10 text-center text-zinc-400 text-sm">
              <Wallet className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No Westpac PayWay configurations yet.
            </CardContent>
          </Card>
        )}
      </div>
      <PayWayDialog dialog={dialog} setDialog={setDialog} reload={reload} />
    </div>
  );
}

/* ══ Page ══════════════════════════════════════════════════════ */
export default function MerchantSettingsPage() {
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    api.get("/xero/master/tenants")
      .then((r) => setTenants(r.data || []))
      .catch(() => setTenants([]));
  }, []);

  const refreshTenants = async () => {
    try {
      await api.post("/xero/master/tenants/refresh");
      const { data } = await api.get("/xero/master/tenants");
      setTenants(data || []);
      toast.success("Xero tenants refreshed");
    } catch (e) { toast.error(errMsg(e, "Refresh failed")); }
  };

  return (
    <div className="p-6 space-y-4 max-w-6xl" data-testid="merchant-settings-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <KeyRound className="h-6 w-6 text-sky-600" />Merchant Settings
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Payment gateway credentials for Stripe (per Xero company), Commbank Simplify and Westpac PayWay.
            All keys are encrypted at rest — you can rotate them any time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-xs">
            <ShieldCheck className="h-3 w-3 mr-1" />Finance & Payroll vault
          </Badge>
          <Button variant="outline" size="sm" onClick={refreshTenants}
            data-testid="merchant-refresh-tenants-btn">
            <RefreshCw className="h-4 w-4 mr-1" />Refresh Xero tenants
          </Button>
        </div>
      </div>

      <Tabs defaultValue="stripe">
        <TabsList>
          <TabsTrigger value="stripe" data-testid="merchant-tab-stripe">
            <CreditCard className="h-4 w-4 mr-1" />Stripe
          </TabsTrigger>
          <TabsTrigger value="simplify" data-testid="merchant-tab-simplify">
            <Landmark className="h-4 w-4 mr-1" />Commbank Simplify
          </TabsTrigger>
          <TabsTrigger value="payway" data-testid="merchant-tab-payway">
            <Wallet className="h-4 w-4 mr-1" />Westpac PayWay
          </TabsTrigger>
        </TabsList>
        <TabsContent value="stripe" className="pt-3">
          <StripeTab tenants={tenants} />
        </TabsContent>
        <TabsContent value="simplify" className="pt-3">
          <SimplifyTab />
        </TabsContent>
        <TabsContent value="payway" className="pt-3">
          <PayWayTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
